import {
  applyNodeChanges,
  getConnectedEdges,
  getOutgoers,
  NodeMouseHandler,
  OnConnect,
  OnConnectEnd,
  OnConnectStart,
  OnNodeDrag,
  OnNodesChange,
  OnNodesDelete,
  useReactFlow,
  useStoreApi,
} from '@xyflow/react';
import { produce } from 'immer';
import { useCallback } from 'react';
import {
  NODES_EXTRA_DATA as NODES_DEFAULT,
  X_OFFSET,
  Y_OFFSET,
} from '../const';
import { useWorkflowStore } from '../store';
import { NodeEnum, type Edge, type Node, type OnNodeAdd } from '../types';
import {
  createEdge,
  createNode,
  getNodesConnectedSourceOrTargetHandleIdsMap,
} from '../utils';
import { useNodeDataUpdate } from './use-node-data-update';
import { useSyncDraft } from './use-sync-draft';
import { useWorkflowReadOnly } from './use-workflow';
export const useNodesInteractions = () => {
  // useStoreApi: 提供对 React Flow 内部状态的访问，用于高级用例和自定义行为
  const store = useStoreApi<Node, Edge>();
  const workflowStore = useWorkflowStore();
  // useWorkflowStore: 自定义 hook，用于访问和管理工作流相关的全局状态
  // const workflowStore = useWorkflowStore();
  const { doSyncWorkflowDraft, debouncedSyncWorkflowDraft } = useSyncDraft();
  const { handleNodeDataUpdateWithSyncDraft } = useNodeDataUpdate();
  // useReactFlow: 提供对 React Flow 实例的访问，允许以编程方式与流程图交互
  const reactflow = useReactFlow();
  // const eventEmitter = useEventEmitter<{ type: 'NODE_CLICK' }>();

  const { getNodesReadOnly } = useWorkflowReadOnly();

  // const dragNodeStartPosition = useRef({ x: 0, y: 0 } as XYPosition);

  const handleNodeEnter = useCallback<NodeMouseHandler>(
    (_, node) => {
      if (getNodesReadOnly()) return;

      const { edges, setEdges } = store.getState();

      const newEdges = produce(edges, (draft) => {
        const connectedEdges = getConnectedEdges([node], edges);

        connectedEdges.forEach((edge) => {
          const currentEdge = draft.find((e) => e.id === edge.id);
          if (!currentEdge) return;

          currentEdge.data._connectedNodeIsHovering = true;
        });
      });
      setEdges(newEdges);
    },
    [store, workflowStore, getNodesReadOnly],
  );

  const handleNodeLeave = useCallback<NodeMouseHandler>(
    (_, node) => {
      if (getNodesReadOnly()) return;

      const { nodes, setNodes, edges, setEdges } = store.getState();
      const newNodes = produce(nodes, (draft) => {
        draft.forEach((node) => {
          node.data._isEntering = false;
        });
      });
      setNodes(newNodes);

      const newEdges = produce(edges, (draft) => {
        draft.forEach((edge) => {
          edge.data._connectedNodeIsHovering = false;
        });
      });
      setEdges(newEdges);
    },
    [store, workflowStore, getNodesReadOnly],
  );

  const handleNodeDragStart: OnNodeDrag<Node> = useCallback(
    (e, node) => {
      const { nodes, setNodes } = store.getState();
      const newNodes = nodes.map((item) => {
        if (item.id === node.id) {
          return {
            ...item,
            position: {
              x: node.position.x,
              y: node.position.y,
            },
          };
        }
        return item;
      });

      setNodes(newNodes);
    },
    [store],
  );

  const handleNodeDrag: OnNodeDrag<Node> = useCallback(
    (_, node: Node) => {
      const { nodes, setNodes } = store.getState();

      const newNodes = produce(nodes, (draft) => {
        const currentNode = draft.find((item) => item.id === node.id)!;

        currentNode.position = {
          x: node.position.x,
          y: node.position.y,
        };
      });

      setNodes(newNodes);
    },
    [store],
  );

  const handleNodeDragStop: OnNodeDrag<Node> = useCallback(
    (e, node) => {
      if (getNodesReadOnly()) return;

      const { nodes, setNodes } = store.getState();

      const newNodes = produce(nodes, (draft) => {
        const currentNode = draft.find((item) => item.id === node.id);
        if (currentNode) {
          currentNode.position = {
            x: node.position.x,
            y: node.position.y,
          };
        }
      });

      setNodes(newNodes);
      console.log('调用拖动结束');
      debouncedSyncWorkflowDraft.run();
    },
    [store, getNodesReadOnly],
  );

  const onNodesChange: OnNodesChange<Node> = useCallback(
    (changes) => {
      const { nodes, setNodes } = store.getState();
      // const newNodes = applyNodeChanges(changes, nodes);
      const newNodes = applyNodeChanges(changes, nodes);
      setNodes(newNodes);
    },
    [store],
  );

  const handleNodeAdd = useCallback<OnNodeAdd>(
    (
      { nodeType, sourceHandle = 'source', targetHandle = 'target' },
      { prevNodeId, prevNodeSourceHandle, nextNodeId, nextNodeTargetHandle },
    ) => {
      const { nodes, setNodes, edges, setEdges } = store.getState();
      const nodesWithSameType = nodes.filter(
        (node) => node.data.type === nodeType,
      );
      const newNode = createNode({
        data: {
          ...NODES_DEFAULT[nodeType].defaultData,
          title: `${NODES_DEFAULT[nodeType].name} ${
            nodesWithSameType.length === 0 ? '' : nodesWithSameType.length + 1
          }`,
          _selected: true,
        },
        position: {
          x: 200,
          y: 200,
        },
      });
      // 有上一个节点，没有下一个节点
      // 比如 在节点右侧点击handler新增的情况
      if (prevNodeId && !nextNodeId) {
        const prevNodeIndex = nodes.findIndex((node) => node.id === prevNodeId);
        const prevNode = nodes[prevNodeIndex];
        const outgoers = getOutgoers(prevNode, nodes, edges).sort(
          (a, b) => a.position.y - b.position.y,
        );
        const lastOutgoer = outgoers[outgoers.length - 1];
        newNode.data._connectedTargetHandleIds = [targetHandle];
        newNode.data._connectedSourceHandleIds = [];
        newNode.position = {
          x: lastOutgoer
            ? lastOutgoer.position.x
            : prevNode.position.x +
              (prevNode?.measured?.width || 244) +
              X_OFFSET,
          y: lastOutgoer
            ? lastOutgoer.position.y +
              (lastOutgoer?.measured?.height || 244) +
              Y_OFFSET
            : prevNode.position.y,
        };

        const newEdge = createEdge({
          id: `${prevNodeId}-${prevNodeSourceHandle}-${newNode.id}-${targetHandle}`,
          source: prevNodeId,
          target: newNode.id,
          sourceHandle: prevNodeSourceHandle,
          targetHandle: targetHandle,
          data: {
            sourceType: prevNode.data.type,
            targetType: newNode.data.type,
            _connectedNodeIsSelected: true,
          },
        });

        const nodesConnectedSourceOrTargetHandleIdsMap =
          getNodesConnectedSourceOrTargetHandleIdsMap(
            [{ type: 'add', edge: newEdge }],
            nodes,
          );

        const newNodes = produce(nodes, (draft: Node[]) => {
          draft.forEach((node) => {
            node.data._selected = false;
            if (nodesConnectedSourceOrTargetHandleIdsMap[node.id]) {
              node.data = {
                ...node.data,
                ...nodesConnectedSourceOrTargetHandleIdsMap[node.id],
              };
            }
          });
          draft.push(newNode);
        });
        setNodes(newNodes);

        const newEdges = produce(edges, (draft) => {
          draft.forEach((item) => {
            if (!item.data) throw new Error('edge data is undefined');
            item.data = {
              ...item.data,
              _connectedNodeIsSelected: false,
              _connectedNodeIsHovering: false,
            };
          });
          draft.push(newEdge);
        });
        setEdges(newEdges);
      }

      doSyncWorkflowDraft();

      console.log('调用添加节点');

      // 有上一个节点，也有下一个节点
      // 比如在 两个节点已经连线的情况下，在edge上添加节点
      // 这时就要把 target 节点替换为新节点
    },
    [store],
  );

  const handleNodeConnect = useCallback<OnConnect>(
    ({ source, sourceHandle, target, targetHandle }) => {
      if (source === target) return;
      if (getNodesReadOnly()) return;

      const { nodes, setNodes, edges, setEdges } = store.getState();
      const targetNode = nodes.find((node) => node.id === target!);
      const sourceNode = nodes.find((node) => node.id === source!);
      if (targetNode?.parentId !== sourceNode?.parentId) return;

      const needDeleteEdges = edges.filter((edge) => {
        return false;
      });
      const needDeleteEdgesIds = needDeleteEdges.map((edge) => edge.id);

      const newEdge = createEdge({
        id: `${source}-${sourceHandle}-${target}-${targetHandle}`,
        source,
        target,
        sourceHandle,
        targetHandle,
        data: {
          sourceType: nodes.find((node) => node.id === source)!.data.type,
          targetType: nodes.find((node) => node.id === target)!.data.type,
        },
      });

      const nodesConnectedSourceOrTargetHandleIdsMap =
        getNodesConnectedSourceOrTargetHandleIdsMap(
          [
            ...needDeleteEdges.map((edge) => ({
              type: 'remove' as const,
              edge,
            })),
            { type: 'add' as const, edge: newEdge },
          ],
          nodes,
        );
      const newNodes = produce(nodes, (draft: Node[]) => {
        draft.forEach((node) => {
          if (nodesConnectedSourceOrTargetHandleIdsMap[node.id]) {
            node.data = {
              ...node.data,
              ...nodesConnectedSourceOrTargetHandleIdsMap[node.id],
            };
          }
        });
      });
      setNodes(newNodes);
      const newEdges = produce(edges, (draft) => {
        const filtered = draft.filter(
          (edge) => !needDeleteEdgesIds.includes(edge.id),
        );

        filtered.push(newEdge);

        return filtered;
      });
      setEdges(newEdges);
    },
    [getNodesReadOnly, store],
  );

  const handleNodeConnectStart = useCallback<OnConnectStart>(
    (_, { nodeId, handleType, handleId }) => {
      if (getNodesReadOnly()) return;
    },
    [store, workflowStore, getNodesReadOnly],
  );

  const handleNodeConnectEnd = useCallback<OnConnectEnd>(
    (e) => {
      if (getNodesReadOnly()) return;
    },
    [store, handleNodeConnect, getNodesReadOnly, workflowStore, reactflow],
  );

  const handleNodeSelect = useCallback(
    (params: { nodeId: string; cancelSelection?: boolean; sync?: boolean }) => {
      const { nodeId, cancelSelection } = params;
      const { nodes, setNodes, edges, setEdges } = store.getState();

      const selectedNode = nodes.find((node) => node.data._selected);

      if (!cancelSelection && selectedNode?.id === nodeId) return;

      const newNodes = produce(nodes, (draft) => {
        draft.forEach((node) => {
          if (node.id === nodeId) {
            node.data._selected = !cancelSelection;
          } else node.data._selected = false;
        });
      });
      setNodes(newNodes);

      const connectedEdges = getConnectedEdges(
        [{ id: nodeId } as Node],
        edges,
      ).map((edge) => edge.id);

      const newEdges = produce(edges, (draft) => {
        draft.forEach((edge) => {
          if (connectedEdges.includes(edge.id)) {
            if (!edge.data) return;
            edge.data = {
              ...edge.data,
              _connectedNodeIsSelected: !cancelSelection,
            };
          } else {
            if (!edge.data) return;
            edge.data = {
              ...edge.data,
              _connectedNodeIsSelected: false,
            };
          }
        });
      });
      setEdges(newEdges);

      if (params.sync) doSyncWorkflowDraft();
    },
    [store],
  );

  const handleNodeClick = useCallback<NodeMouseHandler>(
    (_, node) => {
      const eventEmitter = workflowStore.getState().eventEmitter;
      eventEmitter?.emit({ type: 'NODE_CLICK', payload: { node_id: node.id } });
      handleNodeSelect({ nodeId: node.id, sync: true });
    },
    [handleNodeSelect, workflowStore],
  );

  const handleNodeDelete = useCallback(
    (nodeId: string) => {
      if (getNodesReadOnly()) return;

      const { nodes, setNodes, edges, setEdges } = store.getState();

      const currentNodeIndex = nodes.findIndex((node) => node.id === nodeId);
      const currentNode = nodes[currentNodeIndex];

      if (!currentNode) return;

      // 把是否可删除写到node属性上？
      if (currentNode.data.type === NodeEnum.Start) return;

      const connectedEdges = getConnectedEdges([{ id: nodeId } as Node], edges);

      // Get the map of connected nodes and their handle IDs
      const nodesConnectedSourceOrTargetHandleIdsMap =
        getNodesConnectedSourceOrTargetHandleIdsMap(
          connectedEdges.map((edge) => ({ type: 'remove', edge })),
          nodes,
        );
      // Create new nodes array with updated data and removed node
      const newNodes = produce(nodes, (draft: Node[]) => {
        draft.forEach((node) => {
          if (nodesConnectedSourceOrTargetHandleIdsMap[node.id]) {
            node.data = {
              ...node.data,
              ...nodesConnectedSourceOrTargetHandleIdsMap[node.id],
            };
          }
        });
        draft.splice(currentNodeIndex, 1);
      });
      setNodes(newNodes);
      const newEdges = produce(edges, (draft) => {
        return draft.filter((edge) =>
          connectedEdges.find((connectedEdge) => connectedEdge.id !== edge.id),
        );
      });
      setEdges(newEdges);

      doSyncWorkflowDraft();
    },
    [getNodesReadOnly, store],
  );

  const handleNodesDelete = useCallback<OnNodesDelete>(
    (nodes) => {
      if (getNodesReadOnly()) return;
      nodes.forEach((node) => handleNodeDelete(node.id));
    },
    [store, getNodesReadOnly, handleNodeDelete],
  );

  return {
    handleNodeDragStart,
    handleNodeDrag,
    handleNodeDragStop,
    onNodesChange,
    handleNodeAdd,
    handleNodeSelect,
    handleNodeClick,

    handleNodeConnectStart,
    handleNodeConnect,
    handleNodeConnectEnd,
    handleNodeDelete,
    handleNodesDelete,

    handleNodeEnter,
    handleNodeLeave,
  };
};
